import React from 'react';

const initialForm = {
    name: '',
    phone: '',
    email: '',
    serviceInterest: false,
    termsAccepted: false,
};

function IntakeForm() {
    const [formData, setFormData] = React.useState(initialForm);
    const [status, setStatus] = React.useState('idle');
    const [message, setMessage] = React.useState('');

    const updateField = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData((current) => ({
            ...current,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setStatus('submitting');
        setMessage('');

        try {
            const response = await fetch('/api/intake-form', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || payload.responsecode !== 200) {
                throw new Error(payload.message || 'We could not submit the form.');
            }
            setStatus('success');
            setFormData(initialForm);
            setMessage('Thank you. Your intake form has been submitted.');
        } catch (error) {
            setStatus('error');
            setMessage(error.message || 'We could not submit the form. Please try again.');
        }
    };

    const canSubmit = formData.name.trim() && formData.phone.trim() && formData.email.trim()
        && formData.serviceInterest && formData.termsAccepted && status !== 'submitting';

    return (
        <main className="min-h-screen bg-[#f8f4ec] px-4 py-12 text-slate-900">
            <section className="mx-auto max-w-2xl rounded-3xl bg-white p-6 shadow-xl sm:p-10">
                <p className="mb-2 text-sm font-semibold uppercase tracking-[0.25em] text-amber-700">Expat Elite Singles</p>
                <h1 className="mb-3 text-3xl font-bold sm:text-4xl">Intake Form</h1>
                <p className="mb-8 text-base text-slate-600">
                    Please submit this form only if you are interested in finding a spouse through our matchmaking service.
                </p>

                <form className="space-y-5" onSubmit={handleSubmit}>
                    <label className="block">
                        <span className="mb-1 block text-sm font-semibold">Name</span>
                        <input
                            className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-200"
                            name="name"
                            value={formData.name}
                            onChange={updateField}
                            required
                            autoComplete="name"
                        />
                    </label>

                    <label className="block">
                        <span className="mb-1 block text-sm font-semibold">Phone number</span>
                        <input
                            className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-200"
                            name="phone"
                            type="tel"
                            value={formData.phone}
                            onChange={updateField}
                            required
                            autoComplete="tel"
                        />
                    </label>

                    <label className="block">
                        <span className="mb-1 block text-sm font-semibold">Email address</span>
                        <input
                            className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-200"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={updateField}
                            required
                            autoComplete="email"
                        />
                    </label>

                    <label className="flex gap-3 rounded-2xl bg-slate-50 p-4 text-sm leading-6 text-slate-700">
                        <input
                            className="mt-1 h-4 w-4"
                            name="serviceInterest"
                            type="checkbox"
                            checked={formData.serviceInterest}
                            onChange={updateField}
                            required
                        />
                        <span>I understand that Expat Elite Singles is a matchmaking service, and I am submitting this form because I am interested in finding a spouse.</span>
                    </label>

                    <label className="flex gap-3 text-sm text-slate-700">
                        <input
                            className="mt-1 h-4 w-4"
                            name="termsAccepted"
                            type="checkbox"
                            checked={formData.termsAccepted}
                            onChange={updateField}
                            required
                        />
                        <span>I agree to the <a className="font-semibold text-amber-700 underline" href="/terms">terms and conditions</a>.</span>
                    </label>

                    <button
                        className="w-full rounded-xl bg-slate-950 px-5 py-3 font-bold text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60"
                        type="submit"
                        disabled={!canSubmit}
                    >
                        {status === 'submitting' ? 'Submitting...' : 'Submit intake form'}
                    </button>

                    {message && (
                        <p className={`rounded-xl p-3 text-sm ${status === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>
                            {message}
                        </p>
                    )}
                </form>
            </section>
        </main>
    );
}

export default IntakeForm;
